import React, { useMemo, useRef, useState, useEffect } from "react";
import * as echarts from "echarts";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { apiAxios } from "../api/mockApi";

// cms/pages/Usage.jsx

const initialStats = {
  totalVisitors: 0,
  uniqueVisitors: 0,
  pageViews: 0,
  avgStayTime: "00:00",
  device: {
    pc: 0,
    mobile: 0,
    tablet: 0,
  },
  browser: [],
};

const emptyDailyData = [];
const emptyHourlyData = Array.from({ length: 24 }, () => 0);

const STAT_HELP = {
  totalVisitors:
    "조회 기간 동안 발생한 전체 방문 횟수입니다. 같은 사용자가 여러 번 접속하면 중복 포함하여 집계됩니다.",
  uniqueVisitors:
    "조회 기간 동안 중복을 제거한 방문자 수입니다. 세션, 쿠키, IP 또는 서버 기준에 따라 실제 사람 수와 차이가 있을 수 있습니다.",
  avgStayTime:
    "방문자가 홈페이지에 머문 평균 시간입니다. 서버에서 평균 체류시간이 내려오면 해당 값을 사용하고, 없으면 기본값으로 표시됩니다.",
};

function toYmd8(date = "") {
  return String(date || "").replaceAll("-", "");
}

function toNumber(v, d = 0) {
  const n = Number(String(v ?? "").replaceAll(",", ""));
  return Number.isFinite(n) ? n : d;
}

function formatNumber(n) {
  return Number(n || 0).toLocaleString();
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toDateInputValue(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function diffDaysInclusive(startYmd, endYmd) {
  const start = new Date(startYmd);
  const end = new Date(endYmd);
  const diff = Math.floor((end - start) / (1000 * 60 * 60 * 24));
  return diff + 1;
}

// 현재 날짜 기준 빠른 기간 계산
function getQuickRange(key) {
  const today = new Date();
  const end = today;
  let start;

  if (key === "1개월") {
    start = new Date(today);
    start.setMonth(start.getMonth() - 1);
  } else {
    // 1주: 오늘 포함 최근 7일
    start = addDays(today, -6);
  }

  return {
    dateFrom: toDateInputValue(start),
    dateTo: toDateInputValue(end),
    quick: key,
  };
}

// 현재 조회 기간과 동일한 길이의 이전 기간 계산
function getPreviousRange(search) {
  const days = diffDaysInclusive(search.dateFrom, search.dateTo);

  const currentStart = new Date(search.dateFrom);
  const prevEnd = addDays(currentStart, -1);
  const prevStart = addDays(prevEnd, -(days - 1));

  return {
    ...search,
    dateFrom: toDateInputValue(prevStart),
    dateTo: toDateInputValue(prevEnd),
  };
}

function percentChange(current, previous) {
  const cur = toNumber(current, 0);
  const prev = toNumber(previous, 0);

  if (prev === 0 && cur === 0) return 0;
  if (prev === 0 && cur > 0) return 100;

  return Number((((cur - prev) / prev) * 100).toFixed(1));
}

function parseStayTimeToSeconds(value) {
  const str = String(value ?? "00:00");

  const parts = str.split(":").map((v) => Number(v));
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }

  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }

  return 0;
}

function getRateType(rate) {
  return toNumber(rate, 0) >= 0 ? "up" : "down";
}

function formatRate(rate) {
  return `${Math.abs(toNumber(rate, 0))}%`;
}

function getVisitorKey(r) {
  const ip =
    r.IP ??
    r.REMOTE_ADDR ??
    r.USER_IP ??
    r.CLIENT_IP ??
    r.USER_IP_ADDR ??
    r.CONN_IP ??
    "";

  const agent =
    r.USER_AGENT ??
    r.AGENT ??
    r.BROWSER_AGENT ??
    r.UA ??
    "";

  // 순 방문자 기준은 SESSIONID보다 IP + AGENT를 우선 사용
  // SESSIONID가 방문마다 새로 생성되면 총 방문자수와 순 방문자수가 같아질 수 있음
  if (ip && agent) {
    return `${ip}_${agent}`;
  }

  if (ip) {
    return String(ip);
  }

  if (agent) {
    return String(agent);
  }

  const visitorId =
    r.VISITOR_ID ??
    r.VISITORID ??
    r.COOKIE_ID ??
    r.CLIENT_ID ??
    r.SESSIONID ??
    r.SESSION_ID ??
    r.SESS_ID ??
    r.USER_ID ??
    "";

  return visitorId ? String(visitorId) : "";
}

function getVisitDateValue(r) {
  return String(
    r.VISIT_DT ??
      r.VISIT_DATE ??
      r.VISIT_TIME ??
      r.CONN_DT ??
      r.CONNECT_DT ??
      r.ACCESS_DT ??
      r.REG_DT ??
      r.FRST_PNTTM ??
      r.CREAT_DT ??
      r.DATE ??
      r.date ??
      ""
  );
}

// 방문 로그 목록을 일자별 방문 추이 데이터로 변환
function makeDailyDataFromVisitList(list = []) {
  if (!Array.isArray(list) || !list.length) return [];

  const map = new Map();

  list.forEach((r) => {
    const rawDate = getVisitDateValue(r);

    // "2026-05-18 10:52:50" 또는 "2026-05-18T10:52:50"에서 날짜만 추출
    const match = rawDate.match(/\d{4}-\d{2}-\d{2}/);
    if (!match) return;

    const ymd = match[0];

    const row = map.get(ymd) || {
      date: ymd.slice(5),
      total: 0,
      uniqueSet: new Set(),
    };

    // 전체 방문자수: 방문 로그 row 수 기준
    row.total += 1;

    // 순 방문자수: IP + AGENT 또는 방문자 식별값 기준 중복 제거
    const visitorKey = getVisitorKey(r);

    if (visitorKey) {
      row.uniqueSet.add(visitorKey);
    }

    map.set(ymd, row);
  });

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, r]) => ({
      date: r.date,
      total: r.total,
      unique: r.uniqueSet.size,
    }));
}

// 방문 로그 목록을 시간대별 방문 현황 데이터로 변환
function makeHourlyDataFromVisitList(list = []) {
  if (!Array.isArray(list) || !list.length) {
    return Array.from({ length: 24 }, () => 0);
  }

  const hours = Array.from({ length: 24 }, () => 0);

  list.forEach((r) => {
    const rawDate = getVisitDateValue(r);

    const match = rawDate.match(/\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/);
    if (!match) return;

    const d = new Date(match[0].replace(" ", "T"));
    const hour = d.getHours();

    if (!Number.isNaN(hour) && hour >= 0 && hour <= 23) {
      hours[hour] += 1;
    }
  });

  return hours;
}

// 방문 로그에서 User-Agent 값 추출
function getAgentValue(r) {
  return String(
    r.USER_AGENT ??
      r.AGENT ??
      r.BROWSER_AGENT ??
      r.UA ??
      ""
  );
}

// User-Agent 기준 기기 구분
function detectDevice(agent = "") {
  const ua = String(agent).toLowerCase();

  if (/ipad|tablet|tab/.test(ua)) return "tablet";
  if (/mobile|iphone|android|phone|ipod/.test(ua)) return "mobile";

  return "pc";
}

// 방문 로그 list → 기기별 방문자 수 계산
function makeDeviceStatsFromVisitList(list = []) {
  const result = {
    pc: 0,
    mobile: 0,
    tablet: 0,
  };

  if (!Array.isArray(list) || !list.length) return result;

  list.forEach((r) => {
    const agent = getAgentValue(r);
    const device = detectDevice(agent);

    if (device === "mobile") {
      result.mobile += 1;
    } else if (device === "tablet") {
      result.tablet += 1;
    } else {
      result.pc += 1;
    }
  });

  return result;
}

// User-Agent 기준 브라우저 구분
function detectBrowser(agent = "") {
  const ua = String(agent).toLowerCase();

  if (ua.includes("edg/") || ua.includes("edge/")) return "Edge";
  if (ua.includes("opr/") || ua.includes("opera")) return "Opera";
  if (ua.includes("firefox/")) return "Firefox";

  // Chrome은 Safari 문자열도 포함하는 경우가 많으므로 Safari보다 먼저 체크
  if (ua.includes("chrome/") || ua.includes("crios/")) return "Chrome";

  if (ua.includes("safari/")) return "Safari";

  return "기타";
}

// 방문 로그 list → 브라우저별 방문자 수 계산
function makeBrowserListFromVisitList(list = []) {
  if (!Array.isArray(list) || !list.length) return [];

  const map = new Map();

  list.forEach((r) => {
    const agent = getAgentValue(r);
    const browser = detectBrowser(agent);

    map.set(browser, (map.get(browser) || 0) + 1);
  });

  const total = Array.from(map.values()).reduce((sum, count) => sum + count, 0);

  return Array.from(map.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name, count]) => ({
      name,
      count,
      percent: total ? Number(((count / total) * 100).toFixed(1)) : 0,
    }));
}

function normalizeUsageResponse(res) {
  const stat =
    res?.stat ||
    res?.summary ||
    res?.data?.stat ||
    res?.data?.summary ||
    {};

  const totalVisitors = toNumber(
    stat.TOTAL_VISIT ??
      stat.TOTAL_VISITOR ??
      stat.VISIT_TOTAL ??
      stat.totalVisitors,
    0
  );

  const uniqueVisitors = toNumber(
    stat.UNIQUE_VISIT ??
      stat.UNIQUE_VISITOR ??
      stat.UNIQUE_TOTAL ??
      stat.uniqueVisitors,
    0
  );

  const pc = toNumber(
    stat.PC_VISIT ??
      stat.PC_VISITOR ??
      stat.pc,
    0
  );

  const mobile = toNumber(
    stat.MOBILE_VISIT ??
      stat.MOBILE_VISITOR ??
      stat.mobile,
    0
  );

  const tablet = toNumber(
    stat.TABLET_VISIT ??
      stat.TABLET_VISITOR ??
      stat.tablet,
    0
  );

  // 방문 로그 원본 목록
  const visitList =
    res?.list ||
    res?.data?.list ||
    [];

  // 방문 로그 기준 기기별 방문자 수 계산
  const calculatedDevice = makeDeviceStatsFromVisitList(visitList);

  // 방문 로그 기준 일자별 방문자 추이 계산
  const normalizedDaily = makeDailyDataFromVisitList(visitList);

  const hourlyList =
    res?.hourlyList ||
    res?.data?.hourlyList ||
    [];

  const normalizedHourly = Array.isArray(hourlyList) && hourlyList.length
    ? Array.from({ length: 24 }, (_, hour) => {
        const row = hourlyList.find((r) => {
          const h = toNumber(
            r.HOUR ??
              r.VISIT_HOUR ??
              r.STAT_HOUR ??
              r.hour,
            -1
          );

          return h === hour;
        });

        return toNumber(
          row?.VISIT_COUNT ??
            row?.TOTAL_VISIT ??
            row?.CNT ??
            row?.count,
          0
        );
      })
    : makeHourlyDataFromVisitList(visitList);

  const browserList =
    res?.browserList ||
    res?.data?.browserList ||
    [];

  const normalizedBrowser = Array.isArray(browserList) && browserList.length
    ? browserList.map((r) => ({
        name:
          r.BROWSER ??
          r.BROWSER_NM ??
          r.name ??
          "기타",
        count: toNumber(
          r.VISIT_COUNT ??
            r.CNT ??
            r.count,
          0
        ),
        percent: Number(
          r.PERCENT ??
            r.RATE ??
            r.percent ??
            0
        ),
      }))
    : makeBrowserListFromVisitList(visitList);

  const calculatedTotalVisitors = normalizedDaily.reduce(
    (sum, r) => sum + toNumber(r.total, 0),
    0
  );

  const calculatedUniqueVisitors = normalizedDaily.reduce(
    (sum, r) => sum + toNumber(r.unique, 0),
    0
  );

  return {
    stats: {
      totalVisitors: totalVisitors || calculatedTotalVisitors,
      uniqueVisitors: uniqueVisitors || calculatedUniqueVisitors,
      avgStayTime:
        stat.AVG_STAY_TIME ??
        stat.avgStayTime ??
        "00:00",
      device: {
        pc: pc || calculatedDevice.pc,
        mobile: mobile || calculatedDevice.mobile,
        tablet: tablet || calculatedDevice.tablet,
      },
      browser: normalizedBrowser,
    },
    daily: normalizedDaily,
    hourly: normalizedHourly,
  };
}

function StatCard({
  title,
  value,
  rate,
  type = "up",
  icon,
  helpText,
  compareLabel = "이전 주 대비",
}) {
  const [showHelp, setShowHelp] = useState(false);

  // [추가] 물음표 + 설명 박스 영역 기준
  const helpRef = useRef(null);

  // [추가] 설명 박스 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!showHelp) return;

      // helpRef 영역 내부를 클릭한 경우는 닫지 않음
      if (helpRef.current && helpRef.current.contains(e.target)) {
        return;
      }

      setShowHelp(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showHelp]);

  return (
    <div className="cms-card usage-stat-card">
      <div>
        <div className="usage-stat-title">
          {title}

          {/* [수정] ref 추가: 이 영역 밖을 클릭하면 설명 닫힘 */}
          <span className="usage-help-wrap" ref={helpRef}>
            <button
              type="button"
              className="usage-help-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowHelp((prev) => !prev);
              }}
              aria-label={`${title} 설명 보기`}
            >
              <i className="fa-regular fa-circle-question"></i>
            </button>

            {showHelp && (
              <div className="usage-help-popover">
                <div className="usage-help-popover-title">{title}</div>
                <div className="usage-help-popover-text">{helpText}</div>
              </div>
            )}
          </span>
        </div>

        <div className="usage-stat-value">{value}</div>
        <div className={`usage-stat-rate ${type}`}>
          {type === "up" ? "▲" : "▼"} {rate}
          <span className="text-muted ms-2">{compareLabel}</span>
        </div>
      </div>

      <div className={`usage-stat-icon ${type}`}>
        <i className={icon}></i>
      </div>
    </div>
  );
}

function ChartEmpty({ text = "조회 후 데이터가 표시됩니다." }) {
  return (
    <div className="usage-chart-empty">
      <i className="fa-solid fa-chart-simple"></i>
      <span>{text}</span>
    </div>
  );
}

function LineChartLite({ data }) {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const safeData = Array.isArray(data) ? data : [];

    const xData = safeData.map((d) => d.date);
    const totalData = safeData.map((d) => toNumber(d.total, 0));
    const uniqueData = safeData.map((d) => toNumber(d.unique, 0));

    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "line",
        },
        formatter: function (params) {
          const date = params?.[0]?.axisValue || "";

          const rows = params
            .map((item) => {
              return `
                <div style="display:flex;justify-content:space-between;gap:20px;">
                  <span>${item.marker}${item.seriesName}</span>
                  <strong>${formatNumber(item.value)}명</strong>
                </div>
              `;
            })
            .join("");

          return `
            <div style="min-width:160px;">
              <div style="font-weight:700;margin-bottom:6px;">${date}</div>
              ${rows}
            </div>
          `;
        },
      },
      legend: {
        top: 0,
        left: "center",
        itemWidth: 10,
        itemHeight: 10,
        textStyle: {
          color: "#334155",
          fontSize: 12,
          fontWeight: 600,
        },
        data: ["총 방문자수", "순 방문자수"],
      },
      grid: {
        top: 42,
        left: 52,
        right: 20,
        bottom: 34,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: xData,
        axisTick: {
          show: false,
        },
        axisLine: {
          lineStyle: {
            color: "#dbe3ef",
          },
        },
        axisLabel: {
          color: "#334155",
          fontSize: 12,
          hideOverlap: true,
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        axisLabel: {
          color: "#64748b",
          fontSize: 12,
          formatter: function (value) {
            return formatNumber(value);
          },
        },
        splitLine: {
          lineStyle: {
            color: "#edf2f7",
          },
        },
      },
      series: [
        {
          name: "총 방문자수",
          type: "line",
          smooth: true,
          symbol: "circle",
          symbolSize: 6,
          data: totalData,
          lineStyle: {
            width: 3,
            color: "#0d6efd",
          },
          itemStyle: {
            color: "#0d6efd",
          },
          emphasis: {
            focus: "series",
          },
        },
        {
          name: "순 방문자수",
          type: "line",
          smooth: true,
          symbol: "circle",
          symbolSize: 6,
          data: uniqueData,
          lineStyle: {
            width: 3,
            color: "#20c997",
          },
          itemStyle: {
            color: "#20c997",
          },
          emphasis: {
            focus: "series",
          },
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [data]);

  return (
    <div className="usage-chart-box">
      <div ref={chartRef} className="usage-echart"></div>
    </div>
  );
}

function DevicePieChart({ rows, total }) {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const safeRows = Array.isArray(rows) ? rows : [];

    const chartData = safeRows.map((row) => ({
      name: row.label,
      value: toNumber(row.count, 0),
    }));

    const option = {
      tooltip: {
        trigger: "item",
        formatter: function (params) {
          return `
            <div style="min-width:130px;">
              <div style="font-weight:700;margin-bottom:6px;">${params.name}</div>
              <div style="display:flex;justify-content:space-between;gap:18px;">
                <span>방문자수</span>
                <strong>${formatNumber(params.value)}명</strong>
              </div>
              <div style="display:flex;justify-content:space-between;gap:18px;">
                <span>비율</span>
                <strong>${params.percent}%</strong>
              </div>
            </div>
          `;
        },
      },
      legend: {
        show: false,
      },
      series: [
        {
          name: "기기별 방문자",
          type: "pie",
          radius: ["55%", "78%"],
          center: ["50%", "50%"],
          avoidLabelOverlap: true,
          label: {
            show: false,
          },
          labelLine: {
            show: false,
          },
          emphasis: {
            scale: true,
            scaleSize: 6,
          },
          data: chartData,
        },
      ],
      graphic: [
        {
          type: "text",
          left: "center",
          top: "42%",
          style: {
            text: "총 방문자수",
            textAlign: "center",
            fill: "#64748b",
            fontSize: 12,
            fontWeight: 500,
          },
        },
        {
          type: "text",
          left: "center",
          top: "52%",
          style: {
            text: formatNumber(total),
            textAlign: "center",
            fill: "#0f172a",
            fontSize: 22,
            fontWeight: 800,
          },
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [rows, total]);

  return <div ref={chartRef} className="usage-device-echart"></div>;
}

function HourlyBarChart({ data }) {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const safeData = Array.isArray(data)
      ? data.map((v) => toNumber(v, 0))
      : Array.from({ length: 24 }, () => 0);

    const xData = Array.from({ length: 24 }, (_, i) => `${i}시`);

    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (params) {
          const item = params?.[0];

          if (!item) return "";

          return `
            <div style="min-width:130px;">
              <div style="font-weight:700;margin-bottom:6px;">${item.axisValue}</div>
              <div style="display:flex;justify-content:space-between;gap:18px;">
                <span>방문자수</span>
                <strong>${formatNumber(item.value)}명</strong>
              </div>
            </div>
          `;
        },
      },
      grid: {
        top: 28,
        left: 46,
        right: 18,
        bottom: 34,
      },
      xAxis: {
        type: "category",
        data: xData,
        axisTick: {
          show: false,
        },
        axisLine: {
          lineStyle: {
            color: "#dbe3ef",
          },
        },
        axisLabel: {
          color: "#64748b",
          fontSize: 12,
          interval: 3,
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        axisLabel: {
          color: "#64748b",
          fontSize: 12,
          formatter: function (value) {
            return formatNumber(value);
          },
        },
        splitLine: {
          lineStyle: {
            color: "#edf2f7",
          },
        },
      },
      series: [
        {
          name: "방문자수",
          type: "bar",
          data: safeData,
          barWidth: "58%",
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: "#2f80ed",
          },
          emphasis: {
            itemStyle: {
              color: "#0d6efd",
            },
          },
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [data]);

  return <div ref={chartRef} className="usage-hour-echart"></div>;
}

function BrowserBarChart({ data }) {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const safeData = Array.isArray(data) ? data : [];

    const names = safeData.map((item) => item.name);
    const counts = safeData.map((item) => toNumber(item.count, 0));

    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (params) {
          const item = params?.[0];
          if (!item) return "";

          const row = safeData[item.dataIndex] || {};
          const percent = toNumber(row.percent, 0);

          return `
            <div style="min-width:150px;">
              <div style="font-weight:700;margin-bottom:6px;">${item.axisValue}</div>
              <div style="display:flex;justify-content:space-between;gap:18px;">
                <span>방문자수</span>
                <strong>${formatNumber(item.value)}명</strong>
              </div>
              <div style="display:flex;justify-content:space-between;gap:18px;">
                <span>비율</span>
                <strong>${percent}%</strong>
              </div>
            </div>
          `;
        },
      },
      grid: {
        top: 12,
        left: 70,
        right: 38,
        bottom: 20,
      },
      xAxis: {
        type: "value",
        min: 0,
        axisLabel: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            color: "#edf2f7",
          },
        },
      },
      yAxis: {
        type: "category",
        inverse: true,
        data: names,
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisLabel: {
          color: "#111827",
          fontSize: 13,
          fontWeight: 700,
        },
      },
      series: [
        {
          name: "브라우저 방문자수",
          type: "bar",
          data: counts,
          barWidth: 10,
          itemStyle: {
            borderRadius: [0, 999, 999, 0],
            color: "#0d6efd",
          },
          label: {
            show: true,
            position: "right",
            formatter: function (params) {
              const row = safeData[params.dataIndex] || {};
              return `${toNumber(row.percent, 0)}%`;
            },
            color: "#334155",
            fontSize: 12,
            fontWeight: 700,
          },
          emphasis: {
            itemStyle: {
              color: "#2563eb",
            },
          },
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [data]);

  return <div ref={chartRef} className="usage-browser-echart"></div>;
}

export default function Usage() {
  // const [draft, setDraft] = useState({
  //   dateFrom: "",
  //   dateTo: "",
  //   quick: "1주",
  // });
  const [draft, setDraft] = useState(() => getQuickRange("1주"));

  const [usageStats, setUsageStats] = useState(initialStats);
  const [compareRates, setCompareRates] = useState({
    totalVisitors: 0,
    uniqueVisitors: 0,
    avgStayTime: 0,
  });
  const [usageDailyData, setUsageDailyData] = useState(emptyDailyData);
  const [usageHourlyData, setUsageHourlyData] = useState(emptyHourlyData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 조회 버튼을 누르기 전에는 차트 내용만 표시하지 않음
  const [searched, setSearched] = useState(false);

  const fetchUsage = (search) => {
    return new Promise((resolve, reject) => {
      const url = "/scms/api/sts/selectVisitStats.do";

      const body = JSON.stringify({
        strDate: toYmd8(search.dateFrom),
        endDate: toYmd8(search.dateTo),
        TYPE: "001",
      });

      apiAxios(url, body, function (res) {
        if (res?.apiRes === "Y") {
          resolve(normalizeUsageResponse(res));
        } else {
          reject(new Error(res?.message || "통계 API 응답 실패"));
        }
      });
    });
  };
  const usage = async (search = draft) => {
    try {
      setError(null);
      setLoading(true);

      // 현재 조회 기간
      const current = await fetchUsage(search);

      // 이전 주/월 대비 계산용 이전 기간
      const previousRange = getPreviousRange(search);
      const previous = await fetchUsage(previousRange);

      const currentStaySeconds = parseStayTimeToSeconds(current.stats.avgStayTime);
      const previousStaySeconds = parseStayTimeToSeconds(previous.stats.avgStayTime);

      setUsageStats(current.stats);
      setUsageDailyData(current.daily);
      setUsageHourlyData(current.hourly);

      setCompareRates({
        totalVisitors: percentChange(
          current.stats.totalVisitors,
          previous.stats.totalVisitors
        ),
        uniqueVisitors: percentChange(
          current.stats.uniqueVisitors,
          previous.stats.uniqueVisitors
        ),
        avgStayTime: percentChange(
          currentStaySeconds,
          previousStaySeconds
        ),
      });
    } catch (e) {
      console.warn("[Usage] 통계 API 호출 실패. 빈 통계 데이터로 표시합니다.", e);

      setUsageStats(initialStats);
      setUsageDailyData(emptyDailyData);
      setUsageHourlyData(emptyHourlyData);
      setCompareRates({
        totalVisitors: 0,
        uniqueVisitors: 0,
        avgStayTime: 0,
      });
    } finally {
      setLoading(false);
    }
  };
  const deviceTotal = useMemo(() => {
    const { pc, mobile, tablet } = usageStats.device;
    return pc + mobile + tablet;
  }, [usageStats]);

  const deviceRows = useMemo(() => {
    const { pc, mobile, tablet } = usageStats.device;

    return [
      {
        label: "PC",
        icon: "fa-solid fa-desktop",
        count: pc,
        percent: deviceTotal ? ((pc / deviceTotal) * 100).toFixed(1) : "0.0",
        color: "blue",
      },
      {
        label: "모바일",
        icon: "fa-solid fa-mobile-screen-button",
        count: mobile,
        percent: deviceTotal ? ((mobile / deviceTotal) * 100).toFixed(1) : "0.0",
        color: "green",
      },
      // {
      //   label: "태블릿",
      //   icon: "fa-solid fa-tablet-screen-button",
      //   count: tablet,
      //   percent: deviceTotal ? ((tablet / deviceTotal) * 100).toFixed(1) : "0.0",
      //   color: "orange",
      // },
    ];
  }, [usageStats, deviceTotal]);

  const compareLabel = useMemo(() => {
    if (draft.quick === "1개월") return "이전 월 대비";
    return "이전 주 대비";
  }, [draft.quick]);

  const onSearch = () => {
    if (!draft.dateFrom || !draft.dateTo) {
      alert("기간을 선택해주세요.");
      return;
    }

    if (draft.dateFrom > draft.dateTo) {
      alert("기간(끝)은 기간(시작) 이후여야 합니다.");
      return;
    }

    setSearched(true);
    usage(draft);
  };

  // const onQuickClick = (key) => {
  //   setDraft({ ...draft, quick: key });
  // };

  const onExcelDownload = async () => {
    if (!searched) {
      alert("먼저 기간을 선택하고 조회해주세요.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    workbook.creator = "SCMS";
    workbook.created = new Date();

    const COLOR = {
      blue: "FF0D6EFD",
      dark: "FF0F172A",
      gray: "FFF1F5F9",
      lightBlue: "FFEAF2FF",
      border: "FFE2E8F0",
      white: "FFFFFFFF",
      green: "FF20C997",
      orange: "FFFF922B",
    };

    const border = {
      top: { style: "thin", color: { argb: COLOR.border } },
      left: { style: "thin", color: { argb: COLOR.border } },
      bottom: { style: "thin", color: { argb: COLOR.border } },
      right: { style: "thin", color: { argb: COLOR.border } },
    };

    const center = {
      vertical: "middle",
      horizontal: "center",
    };

    const left = {
      vertical: "middle",
      horizontal: "left",
    };

    const right = {
      vertical: "middle",
      horizontal: "right",
    };

    const applyCell = (cell, options = {}) => {
      cell.border = border;
      cell.alignment = options.alignment || center;

      if (options.fill) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: options.fill },
        };
      }

      if (options.font) {
        cell.font = options.font;
      }
    };

    const applyHeaderRow = (row) => {
      row.eachCell((cell) => {
        applyCell(cell, {
          fill: COLOR.gray,
          font: {
            bold: true,
            color: { argb: COLOR.dark },
          },
        });
      });
    };

    const applyBodyRow = (row) => {
      row.eachCell((cell) => {
        applyCell(cell, {
          alignment: typeof cell.value === "number" ? right : center,
        });

        if (typeof cell.value === "number") {
          cell.numFmt = "#,##0";
        }
      });
    };

    const addTitle = (sheet, title, mergeRange) => {
      sheet.mergeCells(mergeRange);

      const cell = sheet.getCell("A1");
      cell.value = title;
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLOR.blue },
      };
      cell.font = {
        bold: true,
        color: { argb: COLOR.white },
        size: 15,
      };
      cell.alignment = left;

      sheet.getRow(1).height = 30;
    };

    const addSearchInfo = (sheet, startRow = 3) => {
      const rows = [
        ["조회 기간", `${draft.dateFrom} ~ ${draft.dateTo}`, "빠른 기간", draft.quick],
        ["비교 기준", compareLabel, "다운로드 일시", new Date().toLocaleString()],
      ];

      rows.forEach((data, idx) => {
        const row = sheet.getRow(startRow + idx);

        data.forEach((value, colIdx) => {
          const cell = row.getCell(colIdx + 1);
          cell.value = value;

          applyCell(cell, {
            fill: colIdx % 2 === 0 ? COLOR.lightBlue : COLOR.white,
            font:
              colIdx % 2 === 0
                ? {
                    bold: true,
                    color: { argb: COLOR.dark },
                  }
                : {
                    color: { argb: COLOR.dark },
                  },
          });
        });

        row.height = 22;
      });
    };

    const setColumns = (sheet, widths) => {
      widths.forEach((width, idx) => {
        sheet.getColumn(idx + 1).width = width;
      });
    };

    const finishSheet = (sheet, freezeRow = 7) => {
      sheet.views = [
        {
          state: "frozen",
          ySplit: freezeRow,
          showGridLines: false,
        },
      ];

      sheet.eachRow((row) => {
        row.height = row.height || 22;
        row.eachCell((cell) => {
          if (!cell.border) cell.border = border;
        });
      });
    };

    /*
      1. 요약
    */
    const summarySheet = workbook.addWorksheet("요약");
    addTitle(summarySheet, "SCMS 통계관리 요약", "A1:D1");
    addSearchInfo(summarySheet, 3);
    summarySheet.addRow([]);

    const summaryHeader = summarySheet.addRow(["구분", "값", "증감률", "비교 기준"]);
    applyHeaderRow(summaryHeader);

    [
      [
        "총 방문자수",
        usageStats.totalVisitors,
        `${getRateType(compareRates.totalVisitors) === "up" ? "▲" : "▼"} ${formatRate(compareRates.totalVisitors)}`,
        compareLabel,
      ],
      [
        "순 방문자수",
        usageStats.uniqueVisitors,
        `${getRateType(compareRates.uniqueVisitors) === "up" ? "▲" : "▼"} ${formatRate(compareRates.uniqueVisitors)}`,
        compareLabel,
      ],
      [
        "평균 체류시간",
        usageStats.avgStayTime,
        `${getRateType(compareRates.avgStayTime) === "up" ? "▲" : "▼"} ${formatRate(compareRates.avgStayTime)}`,
        compareLabel,
      ],
    ].forEach((data) => {
      const row = summarySheet.addRow(data);
      applyBodyRow(row);
    });

    setColumns(summarySheet, [20, 18, 18, 20]);
    finishSheet(summarySheet);

    /*
      2. 일자별 방문자 추이
    */
    const dailySheet = workbook.addWorksheet("일자별 방문자 추이");
    addTitle(dailySheet, "SCMS 일자별 방문자 추이", "A1:C1");
    addSearchInfo(dailySheet, 3);
    dailySheet.addRow([]);

    const dailyHeader = dailySheet.addRow(["일자", "총 방문자수", "순 방문자수"]);
    applyHeaderRow(dailyHeader);

    usageDailyData.forEach((item) => {
      const row = dailySheet.addRow([
        item.date,
        toNumber(item.total, 0),
        toNumber(item.unique, 0),
      ]);
      applyBodyRow(row);
    });

    dailySheet.autoFilter = {
      from: "A6",
      to: `C${dailySheet.rowCount}`,
    };

    setColumns(dailySheet, [16, 18, 18]);
    finishSheet(dailySheet);

    /*
      3. 시간대별 방문 현황
    */
    const hourlySheet = workbook.addWorksheet("시간대별 방문 현황");
    addTitle(hourlySheet, "SCMS 시간대별 방문 현황", "A1:B1");
    addSearchInfo(hourlySheet, 3);
    hourlySheet.addRow([]);

    const hourlyHeader = hourlySheet.addRow(["시간대", "방문자수"]);
    applyHeaderRow(hourlyHeader);

    usageHourlyData.forEach((count, hour) => {
      const row = hourlySheet.addRow([`${hour}시`, toNumber(count, 0)]);
      applyBodyRow(row);
    });

    hourlySheet.autoFilter = {
      from: "A6",
      to: `B${hourlySheet.rowCount}`,
    };

    setColumns(hourlySheet, [16, 18]);
    finishSheet(hourlySheet);

    /*
      4. 기기별 방문자 비율
    */
    const deviceSheet = workbook.addWorksheet("기기별 방문자 비율");
    addTitle(deviceSheet, "SCMS 기기별 방문자 비율", "A1:C1");
    addSearchInfo(deviceSheet, 3);
    deviceSheet.addRow([]);

    const deviceHeader = deviceSheet.addRow(["기기", "방문자수", "비율"]);
    applyHeaderRow(deviceHeader);

    deviceRows.forEach((item) => {
      const row = deviceSheet.addRow([
        item.label,
        toNumber(item.count, 0),
        `${item.percent}%`,
      ]);
      applyBodyRow(row);
    });

    deviceSheet.autoFilter = {
      from: "A6",
      to: `C${deviceSheet.rowCount}`,
    };

    setColumns(deviceSheet, [16, 18, 14]);
    finishSheet(deviceSheet);

    /*
      5. 브라우저 비율
    */
    const browserSheet = workbook.addWorksheet("브라우저 비율");
    addTitle(browserSheet, "SCMS 브라우저 비율", "A1:C1");
    addSearchInfo(browserSheet, 3);
    browserSheet.addRow([]);

    const browserHeader = browserSheet.addRow(["브라우저", "방문자수", "비율"]);
    applyHeaderRow(browserHeader);

    usageStats.browser.forEach((item) => {
      const row = browserSheet.addRow([
        item.name,
        toNumber(item.count, 0),
        `${item.percent}%`,
      ]);
      applyBodyRow(row);
    });

    browserSheet.autoFilter = {
      from: "A6",
      to: `C${browserSheet.rowCount}`,
    };

    setColumns(browserSheet, [18, 18, 14]);
    finishSheet(browserSheet);

    const fileName = `SCMS_통계관리_${draft.dateFrom}_${draft.dateTo}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
  };

  const onQuickClick = (key) => {
    // 빠른 기간 클릭 시 현재 날짜 기준으로 시작일/종료일도 같이 변경
    setDraft(getQuickRange(key));
  };

  return (
    <div className="cms-card">
      {/* 검색 영역 */}
      <div className="cms-card mb-3 filterBox">
        <div className="row g-2 align-items-end input-rounded">
          <div className="col-md-3 col-6">
            <label className="form-label mb-1">기간(시작)</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={draft.dateFrom}
              onChange={(e) =>
                setDraft({ ...draft, dateFrom: e.target.value })
              }
            />
          </div>

          <div className="col-md-3 col-6">
            <label className="form-label mb-1">기간(끝)</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={draft.dateTo}
              onChange={(e) =>
                setDraft({ ...draft, dateTo: e.target.value })
              }
            />
          </div>

          <div className="col-md-2 col-6">
            <label className="form-label mb-1">빠른 기간</label>
            <div className="usage-quick-btns">
              {[
                { key: "1주", label: "1주" },
                { key: "1개월", label: "1개월" },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`btn btn-sm ${
                    draft.quick === item.key
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => onQuickClick(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="col-md-2 col-12 text-end">
            <div className="d-flex gap-2 justify-content-end">
              <button className="btn btn-sm btn-primary" onClick={onSearch}>
                <i className="fa-solid fa-magnifying-glass me-1"></i>조회
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={onExcelDownload}
              >
                <i className="fa-solid fa-download me-1"></i>엑셀
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 요약 카드: 박스는 항상 표시, 값은 조회 전에는 0/00:00 */}
      <div className="usage-stat-grid">
        <StatCard
          title="총 방문자수"
          value={formatNumber(usageStats.totalVisitors)}
          rate={formatRate(compareRates.totalVisitors)}
          type={getRateType(compareRates.totalVisitors)}
          icon="fa-regular fa-user"
          helpText={STAT_HELP.totalVisitors}
          compareLabel={compareLabel}
        />

        <StatCard
          title="순 방문자수"
          value={formatNumber(usageStats.uniqueVisitors)}
          rate={formatRate(compareRates.uniqueVisitors)}
          type={getRateType(compareRates.uniqueVisitors)}
          icon="fa-regular fa-user"
          helpText={STAT_HELP.uniqueVisitors}
          compareLabel={compareLabel}
        />

        <StatCard
          title="평균 체류시간"
          value={usageStats.avgStayTime}
          rate={formatRate(compareRates.avgStayTime)}
          type={getRateType(compareRates.avgStayTime)}
          icon="fa-regular fa-clock"
          helpText={STAT_HELP.avgStayTime}
          compareLabel={compareLabel}
        />
      </div>

      {/* 메인 차트: 박스는 항상 표시, 그래프는 조회 후 표시 */}
      <div className="usage-dashboard-grid">
        <div className="cms-card usage-panel usage-panel-large">
          <div className="usage-panel-head">
            <h3>일자별 방문자 추이</h3>
          </div>

          {searched ? (
            <LineChartLite data={usageDailyData} />
          ) : (
            <ChartEmpty text="기간 조회 후 일자별 방문자 추이가 표시됩니다." />
          )}
        </div>

        <div className="cms-card usage-panel">
          <div className="usage-panel-head">
            <h3>기기별 방문자 비율</h3>
          </div>

          {searched ? (
            <div className="usage-device-box">
              <DevicePieChart rows={deviceRows} total={deviceTotal} />

              <div className="usage-device-list">
                {deviceRows.map((row) => (
                  <div className="usage-device-row" key={row.label}>
                    <div className="usage-device-name">
                      <i className={`${row.icon} ${row.color}`}></i>
                      <span>{row.label}</span>
                    </div>
                    <strong>{row.percent}%</strong>
                    <span className="text-muted">{formatNumber(row.count)}명</span>
                    <div className="usage-progress">
                      <i
                        className={row.color}
                        style={{ width: `${row.percent}%` }}
                      ></i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <ChartEmpty text="기간 조회 후 기기별 방문자 비율이 표시됩니다." />
          )}
        </div>

        <div className="cms-card usage-panel usage-panel-large">
          <div className="usage-panel-head">
            <h3>시간대별 방문 현황</h3>
          </div>

          {searched ? (
            <HourlyBarChart data={usageHourlyData} />
          ) : (
            <ChartEmpty text="기간 조회 후 시간대별 방문 현황이 표시됩니다." />
          )}
        </div>

        <div className="cms-card usage-panel">
          <div className="usage-panel-head">
            <h3>브라우저 비율</h3>
          </div>

          {searched ? (
            <BrowserBarChart data={usageStats.browser} />
          ) : (
            <ChartEmpty text="기간 조회 후 브라우저 비율이 표시됩니다." />
          )}
        </div>
      </div>
    </div>
  );
}